// Chart option factories extracted from Dashboard.tsx.
// All functions are pure — they take theme + data as arguments and return ApexOptions.

// Single-series area chart (Follower, Following)
// Optional chartColor param lets each chart use its own accent color
export const getThemedAreaChartOptions = (
  categories: string[],
  isDark: boolean,
  chartColor?: string,
): ApexCharts.ApexOptions => {
  const gridLine = isDark ? '#1D293D' : '#F1F5F9';
  const axisLabelColor = isDark ? '#62748E' : '#90A1B9';
  const color = chartColor || (isDark ? '#51A2FF' : '#2B7FFF');
  return {
    chart: {
      type: 'area',
      toolbar: { show: false },
      zoom: { enabled: false },
      background: 'transparent',
      fontFamily: 'Geist, sans-serif',
      sparkline: { enabled: false },
    },
    stroke: { curve: 'smooth', width: 2, colors: [color] },
    dataLabels: { enabled: false },
    colors: [color],
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.35,
        opacityTo: 0.0,
        stops: [0, 100],
        colorStops: [
          { offset: 0, color, opacity: 0.35 },
          { offset: 100, color, opacity: 0 },
        ],
      },
    },
    xaxis: {
      categories,
      labels: { style: { colors: axisLabelColor, fontSize: '11px', fontWeight: 400 }, rotate: 0 },
      axisBorder: { show: true, color: gridLine },
      axisTicks: { show: false },
      crosshairs: { show: true, stroke: { color: isDark ? '#314158' : '#E2E8F0', width: 1, dashArray: 3 } },
    },
    yaxis: {
      labels: { style: { colors: axisLabelColor, fontSize: '11px', fontWeight: 400 } },
      min: 0,
    },
    grid: {
      borderColor: gridLine,
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { left: 8, right: 8, top: 0, bottom: 0 },
    },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      style: { fontSize: '12px' },
      x: { show: true },
      marker: { show: true },
    },
    legend: { show: false },
    markers: { size: 0, hover: { size: 5, sizeOffset: 2 } },
  };
};

// Engagement chart (Follower / Following) — matching Views over time style
export const getEngagementChartOptions = (
  categories: string[],
  data: number[],
  chartColor: string,
  gradientId: string,
  isDark: boolean,
): ApexCharts.ApexOptions => {
  // chartColor kept for API compatibility with existing call sites; not used directly here.
  void chartColor;
  const firstVal = data[0] || 0;
  const lastVal = data[data.length - 1] || 0;
  const dataMin = Math.min(...data.filter(v => v > 0), firstVal || Infinity);
  const dataMax = Math.max(...data, lastVal);
  const dataRange = dataMax - dataMin;
  // If the range is small relative to the values, zoom the Y-axis to show differences clearly
  const yMin = dataRange > 0 && dataMin > 0 && dataRange < dataMin * 0.5
    ? Math.max(0, Math.floor(dataMin - dataRange * 0.5))
    : 0;
  const formatVal = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `${v}`;
  const formatExact = (v: number) => v.toLocaleString();
  const gridColor = isDark ? '#314158' : '#E2E8F0';
  return {
    chart: {
      type: 'area',
      toolbar: { show: false },
      zoom: { enabled: false },
      background: 'transparent',
      fontFamily: 'Geist, sans-serif',
      events: {
        mounted: (chartContext: { el: HTMLElement }) => {
          const svg = chartContext.el.querySelector('svg');
          if (!svg) return;
          let defs = svg.querySelector('defs');
          if (!defs) { defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs'); svg.prepend(defs); }
          if (!defs.querySelector(`#${gradientId}`)) {
            const grad = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
            grad.setAttribute('id', gradientId);
            grad.setAttribute('x1', '0%'); grad.setAttribute('y1', '0%');
            grad.setAttribute('x2', '100%'); grad.setAttribute('y2', '0%');
            const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            stop1.setAttribute('offset', '0%'); stop1.setAttribute('stop-color', '#51A2FF');
            const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            stop2.setAttribute('offset', '100%'); stop2.setAttribute('stop-color', '#00D492');
            grad.appendChild(stop1); grad.appendChild(stop2);
            defs.appendChild(grad);
          }
          const paths = svg.querySelectorAll('.apexcharts-area-series .apexcharts-series path[stroke]');
          paths.forEach((path) => {
            const d = path.getAttribute('d') || '';
            if (d && !d.includes('Z') && path.getAttribute('stroke') !== 'none') {
              path.setAttribute('stroke', `url(#${gradientId})`);
            }
          });
        },
        updated: (chartContext: { el: HTMLElement }) => {
          const svg = chartContext.el.querySelector('svg');
          if (!svg) return;
          let defs = svg.querySelector('defs');
          if (!defs) { defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs'); svg.prepend(defs); }
          if (!defs.querySelector(`#${gradientId}`)) {
            const grad = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
            grad.setAttribute('id', gradientId);
            grad.setAttribute('x1', '0%'); grad.setAttribute('y1', '0%');
            grad.setAttribute('x2', '100%'); grad.setAttribute('y2', '0%');
            const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            stop1.setAttribute('offset', '0%'); stop1.setAttribute('stop-color', '#51A2FF');
            const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            stop2.setAttribute('offset', '100%'); stop2.setAttribute('stop-color', '#00D492');
            grad.appendChild(stop1); grad.appendChild(stop2);
            defs.appendChild(grad);
          }
          const paths = svg.querySelectorAll('.apexcharts-area-series .apexcharts-series path[stroke]');
          paths.forEach((path) => {
            const d = path.getAttribute('d') || '';
            if (d && !d.includes('Z') && path.getAttribute('stroke') !== 'none') {
              path.setAttribute('stroke', `url(#${gradientId})`);
            }
          });
        },
      },
    },
    stroke: { curve: 'smooth', width: 2, colors: ['#51A2FF'] },
    dataLabels: { enabled: false },
    colors: ['#51A2FF'],
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.15,
        opacityTo: 0.0,
        stops: [0, 100],
        colorStops: [
          { offset: 0, color: '#51A2FF', opacity: 0.15 },
          { offset: 100, color: '#00D492', opacity: 0 },
        ],
      },
    },
    xaxis: {
      categories,
      labels: {
        style: { colors: isDark ? '#90A1B9' : '#62748E', fontSize: '14px', fontWeight: 400 },
        rotate: 0,
        formatter: (val: string) => val,
      },
      axisBorder: { show: true, color: gridColor },
      axisTicks: { show: false },
      crosshairs: { show: true, stroke: { color: gridColor, width: 1, dashArray: 3 } },
    },
    yaxis: {
      labels: {
        style: { colors: isDark ? '#90A1B9' : '#62748E', fontSize: '14px', fontWeight: 400 },
        formatter: (val: number) => formatVal(val),
      },
      min: yMin,
    },
    grid: {
      borderColor: gridColor,
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { left: 8, right: 8, top: 24, bottom: -10 },
    },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      style: { fontSize: '12px' },
      x: { show: true },
      marker: { show: true },
    },
    legend: { show: false },
    markers: {
      size: 0,
      discrete: [
        { seriesIndex: 0, dataPointIndex: 0, fillColor: '#51A2FF', strokeColor: isDark ? '#0F172B' : '#FFFFFF', size: 4, shape: 'circle' },
        { seriesIndex: 0, dataPointIndex: data.length - 1, fillColor: '#00D492', strokeColor: isDark ? '#0F172B' : '#FFFFFF', size: 4, shape: 'circle' },
      ],
      hover: { size: 5, sizeOffset: 2 },
    },
    annotations: {
      points: [
        {
          x: categories[0],
          y: firstVal,
          marker: { size: 0 },
          label: {
            text: formatExact(firstVal),
            borderColor: '#155DFC',
            borderWidth: 1,
            borderRadius: 12,
            style: {
              color: '#8EC5FF',
              background: '#162456',
              fontSize: '14px',
              fontWeight: '500',
              padding: { left: 8, right: 8, top: 2, bottom: 2 },
            },
            offsetY: -12,
          },
        },
        {
          x: categories[categories.length - 1],
          y: lastVal,
          marker: { size: 0 },
          label: {
            text: formatExact(lastVal),
            borderColor: '#009966',
            borderWidth: 1,
            borderRadius: 12,
            style: {
              color: '#5EE9B5',
              background: '#022C22',
              fontSize: '14px',
              fontWeight: '500',
              padding: { left: 8, right: 8, top: 2, bottom: 2 },
            },
            offsetY: -12,
          },
        },
      ],
    },
  };
};

// Views over time chart — dedicated options matching Figma
export const getViewsChartOptions = (
  categories: string[],
  data: number[],
  isDark: boolean,
): ApexCharts.ApexOptions => {
  const firstVal = data[0] || 0;
  const lastVal = data[data.length - 1] || 0;
  const formatK = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `${v}`;
  const formatExactV = (v: number) => v.toLocaleString();
  const blueColor = isDark ? '#51A2FF' : '#2B7FFF';
  const orangeColor = isDark ? '#FFBA00' : '#E17100';
  const gridColor = isDark ? '#314158' : '#E2E8F0';
  return {
    chart: {
      type: 'area',
      toolbar: { show: false },
      zoom: { enabled: false },
      background: 'transparent',
      fontFamily: 'Geist, sans-serif',
      events: {
        mounted: (chartContext: { el: HTMLElement }) => {
          const svg = chartContext.el.querySelector('svg');
          if (!svg) return;
          // Add gradient definition for the stroke line
          let defs = svg.querySelector('defs');
          if (!defs) { defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs'); svg.prepend(defs); }
          if (!defs.querySelector('#viewsLineGradient')) {
            const grad = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
            grad.setAttribute('id', 'viewsLineGradient');
            grad.setAttribute('x1', '0%'); grad.setAttribute('y1', '0%');
            grad.setAttribute('x2', '100%'); grad.setAttribute('y2', '0%');
            const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            stop1.setAttribute('offset', '0%'); stop1.setAttribute('stop-color', blueColor);
            const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            stop2.setAttribute('offset', '100%'); stop2.setAttribute('stop-color', orangeColor);
            grad.appendChild(stop1); grad.appendChild(stop2);
            defs.appendChild(grad);
          }
          // Apply gradient to the stroke path
          const paths = svg.querySelectorAll('.apexcharts-area-series .apexcharts-series path[stroke]');
          paths.forEach((path) => {
            const d = path.getAttribute('d') || '';
            if (d && !d.includes('Z') && path.getAttribute('stroke') !== 'none') {
              path.setAttribute('stroke', 'url(#viewsLineGradient)');
            }
          });
        },
        updated: (chartContext: { el: HTMLElement }) => {
          const svg = chartContext.el.querySelector('svg');
          if (!svg) return;
          let defs = svg.querySelector('defs');
          if (!defs) { defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs'); svg.prepend(defs); }
          if (!defs.querySelector('#viewsLineGradient')) {
            const grad = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
            grad.setAttribute('id', 'viewsLineGradient');
            grad.setAttribute('x1', '0%'); grad.setAttribute('y1', '0%');
            grad.setAttribute('x2', '100%'); grad.setAttribute('y2', '0%');
            const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            stop1.setAttribute('offset', '0%'); stop1.setAttribute('stop-color', blueColor);
            const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
            stop2.setAttribute('offset', '100%'); stop2.setAttribute('stop-color', orangeColor);
            grad.appendChild(stop1); grad.appendChild(stop2);
            defs.appendChild(grad);
          }
          const paths = svg.querySelectorAll('.apexcharts-area-series .apexcharts-series path[stroke]');
          paths.forEach((path) => {
            const d = path.getAttribute('d') || '';
            if (d && !d.includes('Z') && path.getAttribute('stroke') !== 'none') {
              path.setAttribute('stroke', 'url(#viewsLineGradient)');
            }
          });
        },
      },
    },
    stroke: {
      curve: 'smooth',
      width: 2,
      colors: [blueColor],
    },
    dataLabels: { enabled: false },
    colors: [blueColor],
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.15,
        opacityTo: 0.0,
        stops: [0, 100],
        colorStops: [
          { offset: 0, color: blueColor, opacity: 0.15 },
          { offset: 100, color: blueColor, opacity: 0 },
        ],
      },
    },
    xaxis: {
      categories,
      labels: {
        style: { colors: isDark ? '#90A1B9' : '#62748E', fontSize: '14px', fontWeight: 400 },
        rotate: 0,
        formatter: (val: string) => val,
      },
      axisBorder: { show: true, color: gridColor },
      axisTicks: { show: false },
      crosshairs: { show: true, stroke: { color: gridColor, width: 1, dashArray: 3 } },
    },
    yaxis: {
      labels: {
        style: { colors: isDark ? '#90A1B9' : '#62748E', fontSize: '14px', fontWeight: 400 },
        formatter: (val: number) => formatK(val),
      },
      min: 0,
    },
    grid: {
      borderColor: gridColor,
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { left: 8, right: 8, top: 24, bottom: -10 },
    },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      style: { fontSize: '12px' },
      x: { show: true },
      marker: { show: true },
    },
    legend: { show: false },
    markers: {
      size: 0,
      discrete: [
        { seriesIndex: 0, dataPointIndex: 0, fillColor: blueColor, strokeColor: isDark ? '#0F172B' : '#FFFFFF', size: 4, shape: 'circle' },
        { seriesIndex: 0, dataPointIndex: data.length - 1, fillColor: orangeColor, strokeColor: isDark ? '#0F172B' : '#FFFFFF', size: 4, shape: 'circle' },
      ],
      hover: { size: 5, sizeOffset: 2 },
    },
    annotations: {
      points: [
        {
          x: categories[0],
          y: firstVal,
          marker: { size: 0 },
          label: {
            text: formatExactV(firstVal),
            borderColor: isDark ? '#155DFC' : '#2B7FFF',
            borderWidth: 1,
            borderRadius: 12,
            style: {
              color: isDark ? '#8EC5FF' : '#2B7FFF',
              background: isDark ? '#162456' : '#EFF6FF',
              fontSize: '14px',
              fontWeight: '500',
              padding: { left: 8, right: 8, top: 2, bottom: 2 },
            },
            offsetY: -12,
          },
        },
        {
          x: categories[categories.length - 1],
          y: lastVal,
          marker: { size: 0 },
          label: {
            text: formatExactV(lastVal),
            borderColor: isDark ? '#E17100' : '#FFD230',
            borderWidth: 1,
            borderRadius: 12,
            style: {
              color: isDark ? '#FFD230' : '#E17100',
              background: isDark ? '#461901' : '#FFFBEB',
              fontSize: '14px',
              fontWeight: '500',
              padding: { left: 8, right: 8, top: 2, bottom: 2 },
            },
            offsetY: -12,
          },
        },
      ],
    },
  };
};

// Radial donut chart (Daily Action Usage) — full circle
export const getRadialChartOptions = (
  isDark: boolean,
  usedLabel: string,
): ApexCharts.ApexOptions => {
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  return {
    chart: { height: 200, type: 'radialBar', background: 'transparent', fontFamily: 'Geist, sans-serif' },
    plotOptions: {
      radialBar: {
        startAngle: 0,
        endAngle: 360,
        hollow: { size: '68%', background: 'transparent' },
        track: {
          background: isDark ? '#314158' : '#E2E8F0',
          strokeWidth: '100%',
        },
        dataLabels: {
          show: true,
          name: { show: true, fontSize: '14px', fontWeight: 400, color: subtitleColor, offsetY: 24 },
          value: {
            show: true,
            fontSize: '36px',
            fontWeight: 700,
            color: titleColor,
            offsetY: -10,
            formatter: (val: number) => `${Math.round(val)}`,
          },
        },
      },
    },
    labels: [usedLabel],
    stroke: { lineCap: 'round' },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'dark',
        type: 'horizontal',
        gradientToColors: ['#00D492'],
        colorStops: [
          { offset: 0, color: '#2B7FFF', opacity: 1 },
          { offset: 100, color: '#00D492', opacity: 1 },
        ],
      },
    },
  };
};
